import axios from 'axios';
import { TRANSACTION_ENTITY_URL } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import { serialiseDateTimeNoTz } from 'Util/AttributeUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { TransactionListSummaryDto } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionsTableComponent';

export interface TransactionFiltersOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate?: boolean;
	statusFilters?: string[];
	locationFilters?: string[];
	createdByFilters?: string[];
	typeFilters?: string[];
	actionFilters?: string[];
	skip?: number;
	take?: number;
	orderBy?: string;
	orderByDescending?: boolean;
	userId?: string;
	initialFetch?: boolean;
	// Return the page without the total (count comes back null). The staff table fetches the total
	// separately with countOnly, keyed without the page, so rows don't wait on it and paging reuses it.
	skipCount?: boolean;
	countOnly?: boolean;
}

export async function FetchTransactionsWithFilters(
	options: TransactionFiltersOptions,
): Promise<{ data: TransactionListSummaryDto[]; count: number | null }> {
	const params = CreateTransactionFilterParams(options);

	const response = await axios.get(`${TRANSACTION_ENTITY_URL}/fetch-filtered-transactions`, { params });

	return {
		data: response.data.data.map((t: any) => t as TransactionListSummaryDto),
		count: response.data.count,
	};
}

export async function FetchTransactionsCount(options: TransactionFiltersOptions): Promise<number> {
	const { count } = await FetchTransactionsWithFilters({ ...options, countOnly: true });
	return count ?? 0;
}

export function CreateTransactionFilterParams(options: TransactionFiltersOptions) {
	const params = new URLSearchParams();

	if (stringNotEmpty(options.searchString)) {
		params.append('searchString', options.searchString);
	}

	if (options.statusFilters && options.statusFilters.length > 0) {
		const statusFilters = [...options.statusFilters];
		// When the Unpaid filter option is hidden (the pay-later feature is off) staff have no way
		// to toggle it, so unpaid transactions would drop out of any explicit status selection.
		// Keep them visible by always including the Unpaid status in the query in that case.
		if (!appConfigStore.ShowUnpaidStatusTag && !statusFilters.includes('PENDING_PAYMENT')) {
			statusFilters.push('PENDING_PAYMENT');
		}
		for (const status of statusFilters) {
			params.append('statusFilters', status);
		}
	}

	if (options.locationFilters && options.locationFilters.length > 0) {
		for (const location of options.locationFilters) {
			params.append('locationFilters', location);
		}
	}

	if (options.createdByFilters && options.createdByFilters.length > 0) {
		for (const createdBy of options.createdByFilters) {
			params.append('createdByFilters', createdBy);
		}
	}

	if (options.typeFilters && options.typeFilters.length > 0) {
		for (const type of options.typeFilters) {
			params.append('typeFilters', type);
		}
	}

	if (options.actionFilters && options.actionFilters.length > 0) {
		for (const action of options.actionFilters) {
			params.append('actionFilters', action);
		}
	}

	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}

	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}

	if (options.filterByCreatedDate !== undefined) {
		params.append('filterByCreatedDate', String(options.filterByCreatedDate));
	}

	if (options.skip !== undefined) {
		params.append('skip', options.skip.toString());
	}

	if (options.take !== undefined) {
		params.append('take', options.take.toString());
	}

	if (options.orderBy !== undefined) {
		params.append('orderBy', options.orderBy.toString());
	}

	if (options.orderByDescending !== undefined) {
		params.append('orderByDescending', String(options.orderByDescending));
	}

	if (options.userId) {
		params.append('userId', options.userId);
	}

	if (options.initialFetch !== undefined) {
		params.append('initialFetch', String(options.initialFetch));
	}

	if (options.skipCount) {
		params.append('skipCount', 'true');
	}

	if (options.countOnly) {
		params.append('countOnly', 'true');
	}

	return params;
}
